// Copyright © 2020  SDG Systems, LLC. All rights reserved.
// This file may not be used in any form without the express written permission of SDG Systems, LLC.

<template>

  <v-container fluid style="height: 100%;">

    <v-layout style="height: 100%;">
      <v-flex id="map-canvas">

        <l-map ref="map" :min-zoom="minZoom" :max-zoom="maxZoom" :center="center" :options="{ zoomDelta: 0.1, zoomSnap: 0.1 }" :crs="crs" style="height: 100%;"
          @update:zoom="zoomUpdated">
          <l-image-overlay :url="mapUrl" :bounds="bounds" />
          <v-marker-cluster>
            <l-marker v-for="thing in things" :key="thing._id" :lat-lng="thingCoords(thing)" :zIndexOffset="1">
              <l-popup :options="{ className: 'popup-content' }">
                <p>{{ thingDescription(thing) }}</p>
                <p>{{ thingEpc(thing) }}</p>
                <p>{{ formatTime(thing) }} </p>
              </l-popup>
            </l-marker>
          </v-marker-cluster>
        </l-map>

      </v-flex>
    </v-layout>

  </v-container>

</template>

<script>
import { mapGetters } from 'vuex'
import moment from 'moment'
import L, { Icon } from 'leaflet'
import { LMap, LImageOverlay, LMarker, LPopup } from 'vue2-leaflet'
import Vue2LeafletMarkerCluster from 'vue2-leaflet-markercluster'
import 'leaflet/dist/leaflet.css'
import 'leaflet.markercluster/dist/MarkerCluster.css'
import 'leaflet.markercluster/dist/MarkerCluster.Default.css'

import {ENDPOINT} from '@/shared/constants'
import ApiRequests from '@/api/ApiRequests'
import {LookupMixin, TimeMixin} from '@/shared/mixins'

delete Icon.Default.prototype._getIconUrl
Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png')
})

export default {
  components: {
    LMap,
    LImageOverlay,
    LMarker,
    LPopup,
    'v-marker-cluster': Vue2LeafletMarkerCluster
  },

  props: {
    searchBarText: String
  },

  mixins: [LookupMixin, TimeMixin],

  data () {
    return {
      hoverThing: null,
      map: null,
      mapImage: null,
      crs: L.CRS.Simple,
      minZoom: -2,
      maxZoom: 4,
      bounds: [[0, 0], [1000, 1000]],
      center: [0, 0],
      things: [],
      refreshIntervalId: null
    }
  },

  computed: {
    ...mapGetters([
      'filters'
    ]),

    mapUrl () {
      return this.map ? ENDPOINT + 'images/' + this.map.image : ''
    },

    mapDescription () {
      return this.map ? this.map.name : ''
    }
  },

  mounted () {
    this.getMap()
  },

  beforeDestroy () {
    if (this.refreshIntervalId) {
      clearInterval(this.refreshIntervalId)
    }
  },

  methods: {
    zoomUpdated (zoom) {
      this.zoom = zoom
    },

    formatTime (thing) {
      return thing ? moment(thing.state.lastEvent).fromNow() : ''
    },

    getMap () {
      ApiRequests.getMaps({ id: this.$route.params.id })
      .then(r => {
        this.map = r.data
        this.loadMapImage()
        this.getThingsForMap()
        this.refreshIntervalId = setInterval(this.getThingsForMap, 10000)
      })
      .catch(e => {
        this.errorText = 'Failed to load map'
      })
    },

    loadMapImage () {
      this.mapImage = new Image()
      this.mapImage.onload = this.prepMap
      this.mapImage.src = this.mapUrl
    },

    prepMap () {
      this.bounds = [ [0, 0], [this.mapImage.naturalHeight, this.mapImage.naturalWidth] ]
      this.$refs.map.fitBounds(this.bounds)
      this.center = [this.mapImage.naturalHeight / 2, this.mapImage.naturalWidth / 2]
    },

    getThingsForMap () {
      ApiRequests.getThingsForMap({ map: this.$route.params.id, search: this.searchBarText })
      .then(r => {
        this.things = r.data
      })
      .catch(e => {
        this.errorText = 'Failed to load locations'
      })
    },

    thingCoords (thing) {
      return [this.mapImage.naturalHeight - (thing.state.xycoordinates[1] * this.mapImage.naturalHeight), thing.state.xycoordinates[0] * this.mapImage.naturalWidth]
    },

    thingEpc (thing) {
      return thing ? this.getThingTags(thing) : ''
    },

    thingDescription (thing) {
      return thing ? thing.common.name : ''
    }

  }
}
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped>

.popup-content p {
  margin: 0px 0px;
}

</style>



// WEBPACK FOOTER //
// src/views/Map.vue